export const staticImagesPathGenerator = (subFolder = '', imageFileName = '') => {

    // Check if the imageFileName is provided
    if (!imageFileName || typeof imageFileName !== "string") return "";
    try {
        if (subFolder) {
            return require(`@/assets/${subFolder}/${imageFileName}`);
        } else {
            return require(`@/assets/${imageFileName}`);
        }
    } catch (error) {
        console.error("Error in staticImagesPathGenerator:", error);
        return "";
    }
};

export async function mappingRecommendationKeys(recommendations) {
    // const recommendations = data.logs.recommendation_log[0].response.detail.recommendations;
    const recommendedProducts = {};

    // Loop through each category in the recommendations
    for (const [category, products] of Object.entries(recommendations)) {
        recommendedProducts[category] = products.map(product => ({
            item_id: product["JANコード"],
            title: product["商品名"],
            image_url: product["images"],
            price: product["参考価格"],
            description: product["商品説明(60文字)"],
            flavour_type: product["タイプ"],
            variant: product["種類"],
            volume: product["容量"],
            volume_variant: product["容量バリアント"],
            child_items: product["その他の容量項目"],
            recommended_way_to_consume: product["飲み頃温度"],
            japanese_food: product["和食"],
            western_food: product["洋食"],
            chinese_and_ethnic_food: product["中華_エスニック"],
            sweets_and_fruit: product["スイーツ_フルーツ"],
            score: product["score"]
        }));
    }

    return recommendedProducts;
}


export async function selectTop3Categories(processedRecommendations, preferredTopCategories = []) {
    try {
        let top3Categories = [];

        // Step 1: Filter categories with items
        const categoriesWithItems = Object.entries(processedRecommendations)
            .reduce((result, [key, value]) => {
                if (value.length > 0) result[key] = value;
                return result;
            }, {});

        // Step 2: Find max score for each category
        const categoriesMaxScore = Object.entries(categoriesWithItems).map(
            ([category, items]) => {
                const maxScore = Math.max(...items.map((item) => item.score));
                return { category, maxScore };
            }
        );

        // Step 3: Sort categories by max score (descending order)
        categoriesMaxScore.sort((a, b) => b.maxScore - a.maxScore);

        // Step 4: Cross-check preferredTopCategories and categoriesWithItems
        const matchedPreferredTopCategories = preferredTopCategories.filter(
            (category) => categoriesWithItems[category]
        );

        // Adding preferred categories to the top of the list
        top3Categories.push(...matchedPreferredTopCategories);

        // Add remaining categories based on score to fill up to 3 total
        const remainingNeeded = 3 - matchedPreferredTopCategories.length;
        if (remainingNeeded > 0) {
            const additionalCategories = categoriesMaxScore
                .filter(({ category }) => !matchedPreferredTopCategories.includes(category))
                .slice(0, remainingNeeded)
                .map(({ category }) => category);

            top3Categories.push(...additionalCategories);
        }

        // console.log("Top 3 categories:", top3Categories);
        return top3Categories;
    } catch (error) {
        console.error("Error in selectTop3Categories:", error);
        throw error;
    }
}

// Track last selected comment information
/* let lastSelectedCommentIndex = -1;
let lastSelectedTasteCategory = null; */

export async function processToDisplayTasteProfileDetails(gptExtractedData, classDetails, recommendationItems, commentIndex) {
    const category = gptExtractedData.result;

    // Get random comment
    /* const selectComment = await getRandomComment(category, classDetails[category].comments); */
    const selectComment = await classDetails[category].comments[commentIndex]
    // Process recommendation data
    const { tasteTypes, manufacturingTypes } = await extractCategoryTypes(recommendationItems);

    // Return formatted data
    return {
        result: category,
        explanation: gptExtractedData.explanation,
        name: classDetails[category]?.name,
        characteristics: classDetails[category]?.characteristics,
        comment: selectComment,
        tasteCategoryTypes: Array.from(tasteTypes),
        manufacturingCategoryTypes: Array.from(manufacturingTypes),
    };
}


/* async function getRandomComment(category, recommendedComments) {
    if (!recommendedComments || recommendedComments.length < 2) {
        return recommendedComments?.[0] || "";
    }

    let randomIndex;
    do {
        randomIndex = Math.floor(Math.random() * recommendedComments.length);
    } while (
        randomIndex === lastSelectedCommentIndex &&
        category === lastSelectedTasteCategory
    );

    lastSelectedCommentIndex = randomIndex;
    lastSelectedTasteCategory = category;

    return recommendedComments[randomIndex];
} */

async function extractCategoryTypes(items) {
    const tasteTypes = new Set();
    const manufacturingTypes = new Set();

    if (items && typeof items === 'object') {
        Object.values(items)
            .filter(Array.isArray)
            .flat()
            .forEach((product) => {
                if (product?.flavour_type?.length > 0) {
                    tasteTypes.add(product.flavour_type.join("・"));
                }
                if (product?.variant?.length > 0) {
                    manufacturingTypes.add(product.variant.join("・"));
                }
            });
    }

    return { tasteTypes, manufacturingTypes };
}

export async function processToDisplayRecommendationItems(
    processedRecommendations,
    top3Categories,
    itemCount = 3
) {
    // Validate inputs parameters
    if (!processedRecommendations || typeof processedRecommendations !== 'object') {
        console.error('Invalid processedRecommendations parameter');
        return {};
    }

    if (!Array.isArray(top3Categories)) {
        console.error('Invalid top3Categories parameter');
        return {};
    }

    if (!itemCount || isNaN(Number(itemCount))) {
        console.error('Invalid itemCount parameter');
        return {};
    }

    try {
        const result = {};
        const firstCategory = top3Categories[0];
        let firstCategoryProducts = (processedRecommendations[firstCategory] || []).slice(0, itemCount);

        result[firstCategory] = [...firstCategoryProducts];

        // If we need more products, fetch from other categories
        let remainingNeeded = itemCount - firstCategoryProducts.length;

        // Try to fill from second category if needed
        if (remainingNeeded > 0 && top3Categories.length > 1) {
            const secondCategory = top3Categories[1];
            const secondCategoryProducts = (processedRecommendations[secondCategory] || [])
                .slice(0, remainingNeeded);

            result[firstCategory] = [...result[firstCategory], ...secondCategoryProducts];
            remainingNeeded -= secondCategoryProducts.length;

            // Try to fill from third category if still needed
            if (remainingNeeded > 0 && top3Categories.length > 2) {
                const thirdCategory = top3Categories[2];
                const thirdCategoryProducts = (processedRecommendations[thirdCategory] || [])
                    .slice(0, remainingNeeded);

                result[firstCategory] = [...result[firstCategory], ...thirdCategoryProducts];
            }
        }

        return result;
    } catch (error) {
        console.error('Error in displayRecommendationItems:', error);
        return {};
    }
}

export async function extractGptResponse(gptResponseString) {
    try {

        // Remove code block markers if present
        if (gptResponseString.startsWith('```')) {
            gptResponseString = gptResponseString
                .replace(/^```(json)?\n/, '')
                .replace(/\n```$/, '');
        }

        // Replace single quotes with double quotes for valid JSON
        const sanitizedString = gptResponseString.replace(/'/g, '"');

        const gptResponseObject = JSON.parse(sanitizedString);

        // Get the result type
        const result = gptResponseObject?.result;

        // Get the explanation
        const explanation = gptResponseObject?.explanation;

        // Process categories
        const categoriesList = gptResponseObject?.categories
            ? gptResponseObject.categories.split(',').map(cat => cat.trim())
            : [];

        // Return structured data
        return {
            result: result,
            explanation: explanation,
            preferredCategories: categoriesList,
        };
    } catch (error) {
        console.error('Error in extractGptResponse:', error);
    }
}